/**
 * Turns what a TanStack query is doing into what the shared field shows.
 *
 * @remarks
 * This is the layer that knows the provider. `@ns/plugin-picker-shared` sits
 * above any transport and must not learn about the proxy, so the classifying
 * happens here and only the RESULT crosses over: a `ChoiceState` plus words
 * from `failureMessage`.
 *
 * @packageDocumentation
 */
// Both guards come from the client, not from `@ns/http-request` directly: that
// package re-exports them precisely so a picker does not depend on the HTTP
// library itself (see its `index.ts` remarks).
import {
  isHttpRequestError,
  isProxyRefusal,
} from '@ns/plugin-arm-proxy-client';
import {
  type ChoiceState,
  type DependsOnState,
  describeDroppedRows,
  type Failure,
  failureMessage,
} from '@ns/plugin-picker-shared';

import type { Page } from './page';

/**
 * Refusal codes that no amount of waiting repairs.
 *
 * @remarks
 * Only `auth-unavailable` is a hiccup at the sign-in service; the other four
 * need the filler to sign in again, or an administrator to grant something. A
 * retry button on those is a button that cannot work.
 */
export const PERMANENT_REFUSAL_CODES: ReadonlySet<string> = new Set([
  'no-credential',
  'expired',
  'consent-revoked',
  'identity-mismatch',
]);

/** What the picker knows when it asks for a state. */
export interface QueryStateInput {
  /** A mistake in the template, which is always fatal. */
  authorError?: string;
  /**
   * What this picker's `dependsOn` resolved to, for a picker that has one.
   *
   * @remarks
   * A picker without dependencies (the subscription picker) leaves this out.
   */
  dependsOn?: DependsOnState;
  /** How many rows of the answer the picker could not offer. Absent counts as none. */
  droppedRowCount?: number;
  error: unknown;
  /** Whether a list is already standing, so a failure need not strand the filler. */
  hasItems?: boolean;
  isError: boolean;
  isPending: boolean;
  /** What the answer said about the rest of the list. */
  page?: Page;
  /** Whether the filler is forced through this field. */
  required?: boolean;
  retry?: () => void;
}

/** Reads the proxy's refusal code off an error, when there is one. */
function refusalCodeOf(error: unknown): string | undefined {
  if (!isProxyRefusal(error)) return undefined;
  return error.body.error.code;
}

/** Describes a caught error in the terms `failureMessage` speaks. */
function describe(error: unknown): Failure {
  const refusalCode = refusalCodeOf(error);
  if (isHttpRequestError(error)) {
    return { kind: 'http', refusalCode, status: error.status };
  }
  return { kind: 'network', refusalCode };
}

/**
 * Whether a failure leaves the filler stuck.
 *
 * @remarks
 * Two gates, either of which makes it fatal:
 *
 * 1. The filler cannot get past this field: it is required and there is nothing
 *    to choose, not even from an earlier answer.
 * 2. There is nothing left to put right: the refusal is one that waiting does
 *    not repair, so offering the list that is still standing would be offering
 *    a field he can never submit.
 */
function isFatal(
  input: QueryStateInput,
  refusalCode: string | undefined,
): boolean {
  const stuck = (input.required ?? false) && !(input.hasItems ?? false);
  const unfixable =
    refusalCode !== undefined && PERMANENT_REFUSAL_CODES.has(refusalCode);
  return stuck || unfixable;
}

/**
 * The state for `ChoiceField`, given what the query is doing.
 *
 * @remarks
 * Order matters and is not arbitrary. An author error outranks everything
 * because nothing was ever asked of the service, so a spinner or a network
 * message would describe a call that never happened. Waiting on another field
 * comes next for the same reason: the query is switched off, so `isPending`
 * would otherwise show a spinner for a call nobody made.
 *
 * @param input - What the picker knows about its own query.
 * @returns The state to hand to `ChoiceField`.
 */
export function queryChoiceState(input: QueryStateInput): ChoiceState {
  if (input.authorError !== undefined) {
    return { kind: 'failed', message: input.authorError, severity: 'fatal' };
  }

  const dependsOn = input.dependsOn;
  if (dependsOn?.kind === 'awaiting') {
    return { kind: 'awaiting', missing: dependsOn.missing };
  }
  if (dependsOn?.kind === 'author-error') {
    return { kind: 'failed', message: dependsOn.message, severity: 'fatal' };
  }

  if (input.isError) {
    const refusalCode = refusalCodeOf(input.error);
    const fatal = isFatal(input, refusalCode);
    // A retry is offered only when waiting can actually help, and only when the
    // caller has something to retry with.
    const temporary =
      refusalCode === undefined || !PERMANENT_REFUSAL_CODES.has(refusalCode);
    const retry = temporary ? input.retry : undefined;

    return {
      kind: 'failed',
      message: failureMessage(describe(input.error)),
      severity: fatal ? 'fatal' : 'warning',
      ...(retry === undefined ? {} : { retry }),
    };
  }

  if (input.isPending) return { kind: 'fetching' };

  const page = input.page;
  const droppedRowCount = input.droppedRowCount ?? 0;
  const notices = [
    ...(page?.hasMore === true
      ? [
          `Showing ${page.shown - droppedRowCount} of ${
            page.total
          }, type to narrow`,
        ]
      : []),
    ...(droppedRowCount > 0 ? [describeDroppedRows(droppedRowCount)] : []),
  ];
  return notices.length === 0
    ? { kind: 'ready' }
    : { kind: 'ready', notice: notices };
}
